import { useEffect, useState, useCallback, useMemo } from "react";
import useAuth from "../../context/AuthProvider";
import {
  StateReportModel_Resp,
  StateReportModel,
  StateReportDocument,
} from "../../models/StateReportModel";
import { apiClient } from "../../helper/api";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Link,
  Box,
  IconButton,
  Button,
} from "@mui/material";
import { useDropzone, FileRejection } from "react-dropzone";
import { FieldAttributes, Form, Formik, useField } from "formik";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { config } from "../../Constants";
import Swal from "sweetalert2";
import { FMTextField } from "../Elements/input";
import React from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { NumericFormat, NumericFormatProps } from "react-number-format";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import moment from "moment";

interface Iprops {
  StateRuleMatrixCL: StateReportModel | undefined;
  handleClose: () => void;
  Open: boolean;
  isStateReport: boolean;
  handleSubmit: () => void;
  selectedTab?: number; // Tab number: 1 = Outstanding Queue, 2 = Completed Queue
}

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}
interface FileAttachment {
  id: string;
  type: string;
  file: File | null;
}

const selectFieldSx = {
  width: "100%",
  "& .MuiSelect-select": { padding: "14.5px 16px" },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "2px solid #e2e8f0 !important",
    borderRadius: "8px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(0, 0, 0, 0.87) !important",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#1976d2 !important",
  },
};

const disabledFieldSx = {
  "&.MuiOutlinedInput-root.Mui-disabled": {
    backgroundColor: "grey.200",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#e2e8f0 !important",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    WebkitTextFillColor: "rgba(0, 0, 0, 0.6)",
    color: "rgba(0, 0, 0, 0.6)",
  },
};
const textFieldSx = {
  "& .MuiOutlinedInput-notchedOutline": {
    border: "2px solid #e2e8f0 !important",
    borderRadius: "8px !important",
  },
  "& .MuiInputBase-input": { padding: "3.5px 14px !important" },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(0, 0, 0, 0.87) !important",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#1976d2 !important",
  },
};
const sectionTitleSx = {
  color: "text.secondary",
  fontWeight: 600,
  letterSpacing: "0.08em",
  display: "block",
  mb: 1,
};
const paperSectionSx = {
  p: 2,
  borderRadius: 2,
  borderColor: "divider",
  backgroundColor: "grey.50",
};

const NumericFormatCustom = React.forwardRef<NumericFormatProps, CustomProps>(
  function NumericFormatCustom(props, ref) {
    const { onChange, ...other } = props;

    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value,
            },
          });
        }}
        thousandSeparator
        valueIsNumericString
        prefix="$"
        decimalScale={2}
        fixedDecimalScale={true}
      />
    );
  },
);

export const AddEditStateReport: React.FC<Iprops> = ({
  handleClose,
  Open,
  StateRuleMatrixCL,
  handleSubmit,
  isStateReport,
  selectedTab,
}) => {
  // Check if this is a new record (no stateReportId)
  const isNewRecord =
    !StateRuleMatrixCL?.stateReportId || StateRuleMatrixCL?.stateReportId === 0;

  const initialValues: StateReportModel = {
    ...StateRuleMatrixCL,
    // Core fields for new records
    largeReport: StateRuleMatrixCL?.largeReport || false,
    stateName: StateRuleMatrixCL?.stateName || "",
    licensee: StateRuleMatrixCL?.licensee || "",
    licenseNumber: StateRuleMatrixCL?.licenseNumber || "", // Required field
    name: StateRuleMatrixCL?.name || "", // Report name starts blank
    period: StateRuleMatrixCL?.period || "",
    status: StateRuleMatrixCL?.status || "Not Started", // Default status for new records
    notes: StateRuleMatrixCL?.notes || "",
    dueDate: StateRuleMatrixCL?.dueDate || "", // Fix for TypeScript error
    address: StateRuleMatrixCL?.address || "",
    instructions: StateRuleMatrixCL?.instructions || "",
    additionalNotes: StateRuleMatrixCL?.additionalNotes || "",
    largeReportFee:
      (StateRuleMatrixCL?.largeReport === true ||
        String(StateRuleMatrixCL?.largeReport).toLowerCase() === "true") &&
      StateRuleMatrixCL?.largeReportFee !== undefined &&
      StateRuleMatrixCL?.largeReportFee !== null &&
      StateRuleMatrixCL?.largeReportFee !== ""
        ? StateRuleMatrixCL.largeReportFee
        : "",
    // Map partnerName to client if client doesn't exist (for billing reports)
    client:
      StateRuleMatrixCL?.client ||
      (StateRuleMatrixCL as any)?.partnerName ||
      "",
    partnersId: StateRuleMatrixCL?.partnersId || 0,
    partnerId: StateRuleMatrixCL?.partnerId || 0,
    // Set defaults for new records
    stateReportId: StateRuleMatrixCL?.stateReportId || 0,
    scheduleId: StateRuleMatrixCL?.scheduleId || 0,
  };

  const { user } = useAuth();
  const token = user?.token;
  // Manage documents locally for delete functionality
  const [documents, setDocuments] = useState<StateReportDocument[]>(
    StateRuleMatrixCL?.documents || [],
  );
  // Update documents when StateRuleMatrixCL changes
  useEffect(() => {
    if (StateRuleMatrixCL?.documents) {
      setDocuments(StateRuleMatrixCL.documents);
    }
  }, [StateRuleMatrixCL?.documents]);

  // File attachments state for upload
  const [attachments, setAttachments] = useState<FileAttachment[]>([
    { id: "1", type: "Report or Invoice", file: null },
    { id: "2", type: "Reconciliation List", file: null },
    { id: "3", type: "Supplemental Attachment 1", file: null },
    { id: "4", type: "Supplemental Attachment 2", file: null },
  ]);
  // Reset attachments when dialog opens/closes
  useEffect(() => {
    if (!Open) {
      setAttachments([
        { id: "1", type: "Report or Invoice", file: null },
        { id: "2", type: "Reconciliation List", file: null },
        { id: "3", type: "Supplemental Attachment 1", file: null },
        { id: "4", type: "Supplemental Attachment 2", file: null },
      ]);
    }
  }, [Open]);

  // File upload handlers
  const handleFileDrop = useCallback(
    (index: number, file: File | null) => {
      const updatedAttachments = [...attachments];
      updatedAttachments[index] = { ...updatedAttachments[index], file };
      setAttachments(updatedAttachments);
    },
    [attachments],
  );

  const removeAttachment = useCallback(
    (index: number) => {
      const updatedAttachments = [...attachments];
      updatedAttachments[index] = { ...updatedAttachments[index], file: null };
      setAttachments(updatedAttachments);
    },
    [attachments],
  );

  const handleViewFile = (file: File) => {
    // Create a blob URL and open in dialog with iframe
    const url = URL.createObjectURL(file);
    setDocumentViewerUrl(url);
    setDocumentViewerFileName(file.name);
    setDocumentViewerOpen(true);
  };

  const handleCloseDocumentViewer = () => {
    if (documentViewerUrl) {
      URL.revokeObjectURL(documentViewerUrl);
    }
    setDocumentViewerOpen(false);
    setDocumentViewerUrl(null);
    setDocumentViewerFileName(null);
  };

  const addMoreAttachments = useCallback(() => {
    const currentLength = attachments.length;
    const maxAllowed = 12;
    const canAdd = Math.min(2, maxAllowed - currentLength);

    if (canAdd > 0) {
      const newAttachments = [];
      for (let i = 0; i < canAdd; i++) {
        const newId = String(currentLength + i + 1);
        const attachmentNumber = currentLength - 2 + i + 1;
        newAttachments.push({
          id: newId,
          type: `Supplemental Attachment ${attachmentNumber}`,
          file: null,
        });
      }
      setAttachments([...attachments, ...newAttachments]);
    }
  }, [attachments]);

  // Handle document deletion
  const handleDeleteDocument = async (
    documentId: number | undefined,
    index: number,
  ) => {
    if (!documentId) {
      // If no documentId, just remove from local state
      const updatedDocuments = documents.filter((_, i) => i !== index);
      setDocuments(updatedDocuments);

      // Update StateRuleMatrixCL if possible (though it's a prop, we'll just update local state)
      if (StateRuleMatrixCL) {
        (StateRuleMatrixCL as any).documents = updatedDocuments;
      }
      return;
    }

    try {
      // Call API to delete document
      const response = await apiClient(
        "/StateReport/DeleteStateReportDocument",
        {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
          body: JSON.stringify({
            stateReportDocumentId: documentId,
          }),
        },
      );

      if (response?.status === true) {
        // Remove from local state
        const updatedDocuments = documents.filter((_, i) => i !== index);
        setDocuments(updatedDocuments);

        // Update StateRuleMatrixCL
        if (StateRuleMatrixCL) {
          (StateRuleMatrixCL as any).documents = updatedDocuments;
        }

        Swal.fire({
          title: "Success!",
          text: "Document deleted successfully",
          icon: "success",
          confirmButtonText: "OK",
        });
      } else {
        throw new Error(response?.message || "Failed to delete document");
      }
    } catch (error: any) {
      console.error("Error deleting document:", error);
      Swal.fire({
        title: "Error",
        text: error?.message || "Failed to delete document. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  // Dropdown data state
  const [partners, setPartners] = useState<
    { partnersId: number; customerName: string }[]
  >([]);
  const [states, setStates] = useState<
    { stateId: number; stateName: string; postalAbbv?: string }[]
  >([]);
  const [dueDates, setDueDates] = useState<{ dueDate: string }[]>([]);
  const [schedules, setSchedules] = useState<
    {
      scheduleId: number;
      reportName: string;
      availablePeriods: {
        timePeriod: string;
        dueDate: string;
        payment?: boolean;
        paymentType?: string;
        report?: boolean;
      }[];
      stateName: string;
    }[]
  >([]);
  const [availablePeriods, setAvailablePeriods] = useState<
    {
      timePeriod: string;
      dueDate: string;
      payment?: boolean;
      paymentType?: string;
      report?: boolean;
    }[]
  >([]);
  const [licensees, setLicensees] = useState<
    {
      licenseeName: string;
      rowNum: string | number;
      licenseNumber: string;
      originalData: any;
    }[]
  >([]);
  const [dueDateParts, setDueDateParts] = useState<{
    month: string;
    day: string;
    year: string;
  }>({ month: "", day: "", year: "" }); // Separate inputs for due date
  // Document viewer state
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [documentViewerUrl, setDocumentViewerUrl] = useState<string | null>(
    null,
  );
  const [documentViewerFileName, setDocumentViewerFileName] = useState<
    string | null
  >(null);
  // Note: periods/schedules now fetched dynamically from the API based on selected state
  const completedStatuses = ["Filed", "No Report Required"];
  // Utility functions
  const shouldDisableDate = (date: any) => {
    if (!date) return false;

    // Convert to Date object if it's a dayjs object
    const dateObj = date instanceof Date ? date : new Date(date);

    const minDate = new Date("1900-01-01");
    const maxDate = new Date("2035-12-31"); // Allow future dates up to 2035
    return dateObj < minDate || dateObj > maxDate;
  };
  // File Dropzone Component
  const FileDropzone = ({
    label,
    onFileDrop,
    acceptedFile,
    onViewFile,
  }: {
    label: string;
    onFileDrop: (file: File | null) => void;
    acceptedFile: File | null;
    onViewFile?: (file: File) => void;
  }) => {
    const [isHovered, setIsHovered] = useState(false);
    const onDropRejected = (fileRejections: FileRejection[]) => {
      const first = fileRejections?.[0];
      const errCodes = first?.errors?.map((e) => e.code) || [];

      if (errCodes.includes("file-too-large")) {
        Swal.fire({
          icon: "error",
          title: "File too large",
          text: "Maximum file size 10MB.",
          confirmButtonText: "OK",
        });
        return;
      }
      if (errCodes.includes("file-invalid-type")) {
        Swal.fire({
          icon: "error",
          title: "Invalid file type",
          text: "Only .pdf, Word, and image files are allowed.",
          confirmButtonText: "OK",
        });
        return;
      }
    };

    const {
      getRootProps,
      getInputProps,
      isFocused,
      isDragAccept,
      isDragReject,
    } = useDropzone({
      maxFiles: 1,
      maxSize: 10 * 1024 * 1024, // 10MB
      accept: {
        "application/pdf": [".pdf"],
        "image/*": [],
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
          [".docx"],
        "application/msword": [".doc"],
      },
      onDrop: (files) => {
        const file = files?.[0];
        if (file) {
          onFileDrop(file);
        }
      },
      onDropRejected,
    });

    const baseStyle: React.CSSProperties = {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px",
      borderWidth: 2,
      borderRadius: 8,
      borderColor: "#B0BEC5",
      borderStyle: "dashed",
      backgroundColor: "#ECEFF1",
      color: "#546E7A",
      outline: "none",
      transition: "border .24s ease-in-out",
      minHeight: "120px",
      cursor: "pointer",
    };

    const focusedStyle: Partial<React.CSSProperties> = {
      borderColor: "#2196f3",
    };

    const acceptStyle: Partial<React.CSSProperties> = {
      borderColor: "#00e676",
    };

    const rejectStyle: Partial<React.CSSProperties> = {
      borderColor: "#ff1744",
    };

    const style = useMemo(
      () =>
        ({
          ...baseStyle,
          ...(isFocused ? focusedStyle : {}),
          ...(isDragAccept ? acceptStyle : {}),
          ...(isDragReject ? rejectStyle : {}),
        }) as React.CSSProperties,
      [isFocused, isDragAccept, isDragReject],
    );

    const handleDropzoneClick = (e: React.MouseEvent) => {
      if (acceptedFile && onViewFile) {
        e.preventDefault();
        e.stopPropagation();
        onViewFile(acceptedFile);
      }
    };

    // Conditionally apply root props - disable click when file is attached
    const rootProps = getRootProps({ style });
    const inputProps = getInputProps();

    // Disable input when file is attached to prevent file picker
    if (acceptedFile && onViewFile) {
      inputProps.disabled = true;
    }

    // Override onClick when file is attached
    const handleBoxClick =
      acceptedFile && onViewFile
        ? handleDropzoneClick
        : (rootProps as any).onClick;

    return (
      <Box
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{ position: "relative" }}
      >
        <Box
          {...rootProps}
          onClick={handleBoxClick}
          sx={acceptedFile && onViewFile ? { cursor: "pointer" } : {}}
        >
          <input {...inputProps} />
          {acceptedFile ? (
            <Box sx={{ textAlign: "center", position: "relative", zIndex: 1 }}>
              <p style={{ margin: 0, fontWeight: "bold", color: "#1976D2" }}>
                {acceptedFile.name}
              </p>
              <p
                style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#666" }}
              >
                {(acceptedFile.size / 1024).toFixed(2)} KB
              </p>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center" }}>
              <p style={{ margin: 0 }}>Click or drag file here</p>
              <p style={{ margin: "4px 0 0 0", fontSize: "12px" }}>{label}</p>
            </Box>
          )}
        </Box>
        {/* Show upload icon when no file and hovering */}
        {!acceptedFile && isHovered && (
          <>
            {/* Transparent black overlay */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                borderRadius: "8px",
                zIndex: 2,
                cursor: "pointer",
              }}
              onClick={(rootProps as any).onClick}
            />
            {/* Upload icon */}
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: "transparent",
                borderRadius: "50%",
                padding: "8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 3,
              }}
              onClick={(rootProps as any).onClick}
            >
              <CloudUploadIcon
                sx={{
                  color: "#ffffff",
                  fontSize: "32px",
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                }}
              />
            </Box>
          </>
        )}
        {/* Show eye icon when file is attached and hovering */}
        {acceptedFile && isHovered && onViewFile && (
          <>
            {/* Transparent black overlay */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                borderRadius: "8px",
                zIndex: 2,
                cursor: "pointer",
              }}
              onClick={handleDropzoneClick}
            />
            {/* Black and white eye icon */}
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: "transparent",
                borderRadius: "50%",
                padding: "8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 3,
              }}
              onClick={handleDropzoneClick}
            >
              <VisibilityIcon
                sx={{
                  color: "#ffffff",
                  fontSize: "32px",
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                }}
              />
            </Box>
          </>
        )}
      </Box>
    );
  };

  // Fetch dropdown data
  const fetchDropdownData = useCallback(async () => {
    if (!isNewRecord) return; // Only fetch for new records

    try {
      const [partnersRes, statesRes, dueDatesRes, schedulesRes] =
        await Promise.all([
          apiClient("/Partners/getPartners", {
            method: "GET",
            headers: new Headers({
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            }),
          }),
          apiClient("/States/getStates", {
            method: "GET",
            headers: new Headers({
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            }),
          }),
          apiClient("/StateReport/getDueDates", {
            method: "GET",
            headers: new Headers({
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            }),
          }),
          apiClient("/PolicyFilings/getAll", {
            method: "GET",
            headers: new Headers({
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            }),
          }),
        ]);

      if (partnersRes?.data) setPartners(partnersRes.data);
      if (statesRes?.data) setStates(statesRes.data);
      if (dueDatesRes?.data) setDueDates(dueDatesRes.data);
      if (schedulesRes?.data) setSchedules(schedulesRes.data);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  }, [token, isNewRecord]);

  // Fetch licensees for specific client and state combination using new API
  const fetchLicensees = useCallback(
    async (partnerId: number, stateAbbreviation: string) => {
      if (!partnerId || !stateAbbreviation || !isNewRecord) return;

      try {
        console.log("=== Fetching Filtered Licensees (New API) ===");
        console.log("Partner ID:", partnerId);
        console.log("State Abbreviation:", stateAbbreviation);

        // Use the new filtered licensees API with state abbreviation
        const response = await apiClient(
          `/StateReport/getLicenseesByClientAndState?partnerId=${partnerId}&stateName=${stateAbbreviation}`,
          {
            method: "GET",
            headers: new Headers({
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            }),
          },
        );

        console.log("New API Response:", response);

        if (response?.data && Array.isArray(response.data)) {
          console.log("Filtered Licensees Count:", response.data.length);
          console.log("Licensees Data:", response.data);

          // Map the response to match our expected format, preserving all licensee data
          const formattedLicensees = response.data.map((licensee: any) => ({
            licenseeName:
              licensee.licenseeName || licensee.licensee || licensee.name,
            rowNum:
              licensee.licenseeId ||
              licensee.id ||
              licensee.rowNum ||
              licensee.licenseNumber,
            licenseNumber:
              licensee.licenseNumber ||
              licensee.licenseNo ||
              licensee.license ||
              "", // Capture license number
            originalData: licensee, // Store complete original data for reference
          }));

          setLicensees(formattedLicensees);
        } else {
          console.log(
            "No licensees found for this client and state combination",
          );
          setLicensees([]);
        }
      } catch (error) {
        console.error("Error fetching filtered licensees:", error);
        setLicensees([]);
      }
    },
    [token, isNewRecord],
  );

  // Fetch periods/schedules for specific state using new filtered API
  const fetchStateSchedules = useCallback(
    async (stateAbbreviation: string) => {
      if (!stateAbbreviation || !isNewRecord) return;

      try {
        console.log("=== Fetching State Schedules (New API) ===");
        console.log("State Abbreviation:", stateAbbreviation);

        // Use the new filtered schedules API
        const response = await apiClient(
          `/StateReport/getSchedulesByState?stateName=${stateAbbreviation}`,
          {
            method: "GET",
            headers: new Headers({
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            }),
          },
        );

        console.log("Filtered Schedules API Response:", response);

        if (response?.data && Array.isArray(response.data)) {
          console.log("State Schedules Count:", response.data.length);
          console.log("Schedules Data:", response.data);

          // Store the schedule data directly (no parsing needed)
          setSchedules(response.data);

          // Clear available periods until a report is selected
          setAvailablePeriods([]);
        } else {
          console.log("No schedules found for this state");
          setSchedules([]);
          setAvailablePeriods([]);
        }
      } catch (error) {
        console.error("Error fetching filtered schedules:", error);
        setSchedules([]);
        setAvailablePeriods([]);
      }
    },
    [token, isNewRecord],
  );

  useEffect(() => {
    fetchDropdownData();
    // Don't fetch licensees on mount - wait for client and state selection
  }, [fetchDropdownData]);

  return (
    <Dialog
      open={Open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          width: "90%",
          maxWidth: 900,
          minHeight: "50vh",
          maxHeight: "90vh",
          borderRadius: 2,
          boxShadow: "0 24px 48px rgba(0,0,0,0.12)",
        },
      }}
      sx={{ "& .MuiDialog-paper": { overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          py: 2,
          px: 3,
          background: "linear-gradient(135deg, #0d4a63 0%, #15607a 100%)",
          color: "#fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <AssessmentOutlinedIcon sx={{ fontSize: 28, opacity: 0.95 }} />
          <Typography
            component="span"
            variant="h6"
            sx={{ fontWeight: 600, letterSpacing: "0.02em" }}
          >
            Edit State Report
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{
            color: "rgba(255,255,255,0.9)",
            "&:hover": {
              color: "#fff",
              backgroundColor: "rgba(255,255,255,0.12)",
            },
          }}
          aria-label="Close dialog"
        >
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>
      <Formik
        key={`${StateRuleMatrixCL?.scheduleId}-${StateRuleMatrixCL?.stateReportId}-${StateRuleMatrixCL?.partnersId}`}
        initialValues={initialValues}
        enableReinitialize={true}
        validateOnChange={true}
        onSubmit={async (data, { setSubmitting, setFieldError }) => {
          // Collect all validation errors
          const errors: { [key: string]: string } = {};

          // Validation: If status is "Filed", Date Completed is required
          if (
            completedStatuses.includes(data?.status || "") &&
            (!data.dateFinalReportSentToClient ||
              data.dateFinalReportSentToClient.trim() === "")
          ) {
            errors.dateFinalReportSentToClient =
              "Date Completed is required when status is Filed";
          }

          // Validation: If Large Report is "Yes", Large Report Fee is required
          if (
            (data.largeReport === true ||
              String(data.largeReport).toUpperCase() === "TRUE") &&
            (!data.largeReportFee ||
              (typeof data.largeReportFee === "string" &&
                data.largeReportFee.trim() === "") ||
              data.largeReportFee === 0)
          ) {
            errors.largeReportFee =
              "Large Report Fee is required when Large Report is Yes";
          }

          // Validation: Required fields (except Notes To Client and Tax Reported)
          if (!data.client || data.client.trim() === "") {
            errors.client = "Client is required";
          }
          if (!data.stateName || data.stateName.trim() === "") {
            errors.stateName = "State is required";
          }
          if (!data.licensee || data.licensee.trim() === "") {
            errors.licensee = "Licensee is required";
          }
          if (!data.licenseNumber || data.licenseNumber.trim() === "") {
            errors.licenseNumber = "License Number is required";
          }
          if (!data.name || data.name.trim() === "") {
            errors.name = "Report Name is required";
          }
          if (!data.period || data.period.trim() === "") {
            errors.period = "Period is required";
          }
          if (!data.status || data.status.trim() === "") {
            errors.status = "Status is required";
          }
          if (!data.dueDate || data.dueDate.trim() === "") {
            errors.dueDate = "Due Date is required";
          }

          // If there are any validation errors, set them all and stop submission
          if (Object.keys(errors).length > 0) {
            Object.keys(errors).forEach((field) => {
              setFieldError(field, errors[field]);
            });
            setSubmitting(false);
            return;
          }

          data.partnerId = data.partnersId;
          data.largeReport = String(data.largeReport).toLowerCase() === "true";
          // Remove hardcoded values - let them come from filing schedule via Period selection
          // data.paymentType = 'N/A'; // Now set from filing schedule
          // data.report = true; // Now set from filing schedule

          // Convert state name to abbreviation for API submission
          const selectedStateObj = states.find(
            (s) => s.stateName === data.stateName,
          );
          const stateAbbreviation =
            selectedStateObj?.postalAbbv || data.stateName;
          data.stateName = stateAbbreviation; // Use abbreviation instead of full name
          if (completedStatuses.includes(data?.status || "")) {
            data.filerName = `${user?.userFirstName} ${user?.userLastName || user?.userlastName}`;
          }

          console.log("=== Form Submission Debug ===");
          console.log("Licensee:", data.licensee);
          console.log("License Number:", data.licenseNumber);
          console.log("State Name (Original):", selectedStateObj?.stateName);
          console.log("State Name (Abbreviation):", data.stateName);
          console.log("Payment Type:", data.paymentType);
          console.log("Report:", data.report);
          console.log("Full Form Data:", data);

          try {
            // Check if there are files to upload (only in Completed Queue - tab 2)
            const filesToUpload =
              selectedTab === 2
                ? attachments.filter((att) => att.file !== null)
                : [];

            if (filesToUpload.length > 0) {
              // Use FormData and new API endpoint for submissions with files
              const formData = new FormData();
              formData.append(
                "data",
                JSON.stringify({
                  ...data,
                  stateReportId: (data as any).stateReportId,
                }),
              );

              filesToUpload.forEach((attachment, index) => {
                if (attachment.file) {
                  formData.append(`file_${index}`, attachment.file);
                  formData.append(`fileType_${index}`, attachment.type);
                  formData.append(`fileName_${index}`, attachment.file.name);
                }
              });
              formData.append("fileCount", filesToUpload.length.toString());

              // Use separate API endpoint for file uploads
              const response = await apiClient(
                "/StateReport/AddEditStateReportWithFiles",
                {
                  method: "POST",
                  headers: new Headers({
                    // Don't set Content-Type header - browser will set it with boundary for FormData
                    Authorization: `Bearer ${token}`,
                  }),
                  body: formData,
                },
              );

              if (response?.status === true) {
                setSubmitting(false);
                await Swal.fire({
                  title: "Data Successfully saved",
                  text: response?.message || "",
                  icon: "success",
                  confirmButtonText: "OK",
                });
                handleSubmit();
              } else {
                throw new Error(response?.message || "Failed to save data");
              }
            } else {
              // No files, use original API endpoint with JSON (unchanged)
              const response = await fetch(
                config + "/StateReport/AddEditStateReport",
                {
                  method: "POST",
                  headers: new Headers({
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                  }),
                  body: JSON.stringify({
                    ...data,
                    stateReportId: (data as any).stateReportId,
                  }),
                },
              );

              if (response.ok) {
                const res = await response.json();
                setSubmitting(false);
                await Swal.fire({
                  title: "Data Successfully saved",
                  text: "",
                  icon: "success",
                  confirmButtonText: "OK",
                });
                handleSubmit();
              } else {
                throw new Error("Something went wrong");
              }
            }
          } catch (error: any) {
            setSubmitting(false);
            console.error("Error saving state report:", error);
            Swal.fire({
              title: "Error",
              text: error?.message || "Failed to save data. Please try again.",
              icon: "error",
              confirmButtonText: "OK",
            });
          }
        }}
      >
        {({ values, isSubmitting, setFieldValue, errors }) => (
          <Form className="main-form">
            <DialogContent
              sx={{
                px: { xs: 2, sm: 3 },
                py: 3,
                overflow: "auto",
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="overline" sx={sectionTitleSx}>
                    Report details
                  </Typography>
                  <Paper variant="outlined" sx={paperSectionSx}>
                    <Grid container spacing={2}>
                      {/* 1. Client Selection - First */}
                      <Grid item xs={12} md={6}>
                  <FormControl sx={{ width: "100%" }}>
                    {isNewRecord ? (
                      <FormControl fullWidth>
                        <InputLabel>Client *</InputLabel>
                        <Select
                          name="client"
                          value={values.client || ""}
                          sx={selectFieldSx}
                          onChange={(e) => {
                            const selectedPartner = partners.find(
                              (p) => p.customerName === e.target.value,
                            );
                            setFieldValue("client", e.target.value);
                            setFieldValue(
                              "partnersId",
                              selectedPartner?.partnersId || 0,
                            );
                            setFieldValue(
                              "partnerId",
                              selectedPartner?.partnersId || 0,
                            );
                            // Clear dependent fields when client changes
                            setFieldValue("licensee", "");
                            setFieldValue("licenseNumber", "");
                            setFieldValue("stateName", "");
                            setFieldValue("name", "");
                            setFieldValue("period", "");
                            setLicensees([]);
                            setSchedules([]);
                            setAvailablePeriods([]);
                          }}
                          label="Client *"
                          IconComponent={ExpandMoreIcon}
                        >
                          <MenuItem value="">Select Client</MenuItem>
                          {partners.map((partner) => (
                            <MenuItem
                              key={partner.partnersId}
                              value={partner.customerName}
                            >
                              {partner.customerName}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.client && (
                          <FormHelperText sx={{ color: "red" }}>
                            {errors.client}
                          </FormHelperText>
                        )}
                      </FormControl>
                    ) : (
                      <FormControl fullWidth>
                        <FMTextField
                          name="client"
                          autoFocus
                          margin="dense"
                          disabled
                          label="Client"
                          placeholder="client"
                          type="text"
                          fullWidth
                          variant="outlined"
                          sx={{ ...textFieldSx, width: "100%" }}
                        />
                      </FormControl>
                    )}
                  </FormControl>
                </Grid>

                {/* 2. State Selection - Second */}
                <Grid item xs={12} md={6}>
                  <FormControl sx={{ width: "100%" }}>
                    {isNewRecord ? (
                      <FormControl fullWidth>
                        <InputLabel>State *</InputLabel>
                        <Select
                          name="stateName"
                          value={values.stateName || ""}
                          sx={!values.client ? { ...selectFieldSx, ...disabledFieldSx } : selectFieldSx}
                          onChange={(e) => {
                            const selectedStateName = e.target.value;
                            const selectedStateObj = states.find(
                              (s) => s.stateName === selectedStateName,
                            );
                            const stateAbbreviation =
                              selectedStateObj?.postalAbbv || selectedStateName;

                            setFieldValue("stateName", selectedStateName);
                            // Clear dependent fields
                            setFieldValue("licensee", "");
                            setFieldValue("licenseNumber", "");
                            setFieldValue("name", "");
                            setFieldValue("period", "");
                            setLicensees([]);
                            setSchedules([]);
                            setAvailablePeriods([]);

                            // Fetch data for this state + client combination
                            if (selectedStateName && values.partnersId) {
                              console.log("=== Triggering Licensee Fetch ===");
                              console.log(
                                "Selected State Name:",
                                selectedStateName,
                              );
                              console.log("State Object:", selectedStateObj);
                              console.log(
                                "State Abbreviation:",
                                stateAbbreviation,
                              );
                              console.log("Partner ID:", values.partnersId);
                              console.log("Client Name:", values.client);
                              fetchLicensees(
                                values.partnersId,
                                stateAbbreviation,
                              );
                              fetchStateSchedules(stateAbbreviation);
                            } else {
                              console.log("=== Cannot Fetch Licensees ===");
                              console.log("Selected State:", selectedStateName);
                              console.log("Partner ID:", values.partnersId);
                              console.log("Client:", values.client);
                            }
                          }}
                          label="State *"
                          IconComponent={ExpandMoreIcon}
                          disabled={!values.client}
                        >
                          <MenuItem value="">Select State</MenuItem>
                          {states.map((state) => (
                            <MenuItem
                              key={state.stateId}
                              value={state.stateName}
                            >
                              {state.stateName}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.stateName && (
                          <FormHelperText sx={{ color: "red" }}>
                            {errors.stateName}
                          </FormHelperText>
                        )}
                      </FormControl>
                    ) : (
                      <FormControl fullWidth>
                        <FMTextField
                          name="stateName"
                          autoFocus
                          margin="dense"
                          disabled
                          label="State"
                          placeholder="State"
                          type="text"
                          fullWidth
                          variant="outlined"
                          sx={{ ...textFieldSx, width: "100%" }}
                        />
                      </FormControl>
                    )}
                  </FormControl>
                </Grid>

                {/* 3. Licensee Selection - Third (after client and state) */}
                <Grid item xs={12} md={6}>
                  <FormControl sx={{ width: "100%" }}>
                    {isNewRecord ? (
                      <FormControl fullWidth>
                        <InputLabel>Licensee *</InputLabel>
                        <Select
                          name="licensee"
                          value={values.licensee || ""}
                          sx={!values.client || !values.stateName ? { ...selectFieldSx, ...disabledFieldSx } : selectFieldSx}
                          onChange={(e) => {
                            const selectedLicenseeName = e.target.value;
                            const selectedLicenseeData = licensees.find(
                              (l) => l.licenseeName === selectedLicenseeName,
                            );

                            setFieldValue("licensee", selectedLicenseeName);

                            // Set the license number from the selected licensee
                            if (selectedLicenseeData) {
                              console.log("=== Selected Licensee ===");
                              console.log(
                                "Licensee Name:",
                                selectedLicenseeName,
                              );
                              console.log(
                                "License Number:",
                                selectedLicenseeData.licenseNumber,
                              );
                              console.log(
                                "Full Licensee Data:",
                                selectedLicenseeData.originalData,
                              );
                              setFieldValue(
                                "licenseNumber",
                                selectedLicenseeData.licenseNumber,
                              );
                            } else {
                              setFieldValue("licenseNumber", "");
                            }
                          }}
                          label="Licensee *"
                          IconComponent={ExpandMoreIcon}
                          disabled={!values.client || !values.stateName}
                        >
                          <MenuItem value="">Select Licensee</MenuItem>
                          {licensees.map((licensee, index) => (
                            <MenuItem key={index} value={licensee.licenseeName}>
                              {licensee.licenseeName}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.licensee && (
                          <FormHelperText sx={{ color: "red" }}>
                            {errors.licensee}
                          </FormHelperText>
                        )}
                      </FormControl>
                    ) : (
                      <FormControl fullWidth>
                        <FMTextField
                          name="licensee"
                          autoFocus
                          margin="dense"
                          disabled
                          label="Licensee"
                          placeholder="Licensee"
                          type="text"
                          fullWidth
                          variant="outlined"
                          sx={{ ...textFieldSx, width: "100%" }}
                        />
                      </FormControl>
                    )}
                  </FormControl>
                </Grid>
                {/* 4. Report Name - Dropdown from state schedules */}
                <Grid item xs={12} md={6}>
                  <FormControl sx={{ width: "100%" }}>
                    {isNewRecord ? (
                      <FormControl fullWidth>
                        <InputLabel>Report Name *</InputLabel>
                        <Select
                          name="name"
                          value={values.name || ""}
                          sx={!values.stateName ? { ...selectFieldSx, ...disabledFieldSx } : selectFieldSx}
                          onChange={(e) => {
                            const selectedReportName = e.target.value;
                            setFieldValue("name", selectedReportName);

                            // Find the selected schedule and populate available periods
                            const selectedSchedule = schedules.find(
                              (s) => s.reportName === selectedReportName,
                            );
                            if (selectedSchedule) {
                              console.log("=== Selected Report ===");
                              console.log("Report Name:", selectedReportName);
                              console.log(
                                "Available Periods:",
                                selectedSchedule.availablePeriods,
                              );
                              console.log(
                                "Available Periods Length:",
                                selectedSchedule.availablePeriods?.length,
                              );

                              // Debug each period
                              selectedSchedule.availablePeriods?.forEach(
                                (period, index) => {
                                  console.log(`Period ${index}:`, period);
                                  console.log(
                                    `Period ${index} timePeriod:`,
                                    period?.timePeriod,
                                  );
                                  console.log(
                                    `Period ${index} dueDate:`,
                                    period?.dueDate,
                                  );
                                },
                              );

                              setAvailablePeriods(
                                selectedSchedule.availablePeriods,
                              );
                              setFieldValue(
                                "scheduleId",
                                selectedSchedule.scheduleId,
                              );
                            } else {
                              setAvailablePeriods([]);
                            }

                            // Clear period and due date when report changes
                            setFieldValue("period", "");
                            setFieldValue("dueDate", "");
                            setDueDateParts({ month: "", day: "", year: "" });
                          }}
                          label="Report Name *"
                          IconComponent={ExpandMoreIcon}
                          disabled={!values.stateName}
                        >
                          <MenuItem value="">Select Report Name</MenuItem>
                          {schedules.map((schedule) => (
                            <MenuItem
                              key={schedule.scheduleId}
                              value={schedule.reportName}
                            >
                              {schedule.reportName}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.name && (
                          <FormHelperText sx={{ color: "red" }}>
                            {errors.name}
                          </FormHelperText>
                        )}
                      </FormControl>
                    ) : (
                      <FormControl fullWidth>
                        <FMTextField
                          name="name"
                          autoFocus
                          margin="dense"
                          disabled
                          label="Report Name"
                          placeholder="Report Name"
                          type="text"
                          fullWidth
                          variant="outlined"
                          sx={{ ...textFieldSx, width: "100%" }}
                        />
                      </FormControl>
                    )}
                  </FormControl>
                </Grid>

                {/* 5. Period - Filtered by selected report's available periods */}
                <Grid item xs={12} md={6}>
                  <FormControl sx={{ width: "100%" }}>
                    {isNewRecord ? (
                      <FormControl fullWidth>
                        <InputLabel>Period *</InputLabel>
                        <Select
                          name="period"
                          value={values.period || ""}
                          sx={!values.name ? { ...selectFieldSx, ...disabledFieldSx } : selectFieldSx}
                          onChange={(e) => {
                            const selectedTimePeriod = e.target.value;
                            setFieldValue("period", selectedTimePeriod);

                            // Find the selected period and set the due date parts
                            const selectedPeriodData = availablePeriods.find(
                              (p) => p.timePeriod === selectedTimePeriod,
                            );
                            if (selectedPeriodData) {
                              console.log("=== Period Selected ===");
                              console.log(
                                "Selected Period:",
                                selectedTimePeriod,
                              );
                              console.log(
                                "Due Date:",
                                selectedPeriodData.dueDate,
                              );
                              console.log(
                                "Payment Required:",
                                selectedPeriodData.payment,
                              );
                              console.log(
                                "Payment Type:",
                                selectedPeriodData.paymentType,
                              );
                              console.log(
                                "Report Required:",
                                selectedPeriodData.report,
                              );

                              // Parse the due date into parts
                              const dueDateObj = new Date(
                                selectedPeriodData.dueDate,
                              );
                              const month = (dueDateObj.getMonth() + 1)
                                .toString()
                                .padStart(2, "0"); // 01-12
                              const day = dueDateObj
                                .getDate()
                                .toString()
                                .padStart(2, "0"); // 01-31
                              const year = dueDateObj.getFullYear().toString(); // 2025

                              // Set the due date parts
                              setDueDateParts({ month, day, year });

                              // Also set the combined due date for form validation
                              const formattedDueDate = `${month}/${day}/${year}`;
                              setFieldValue("dueDate", formattedDueDate);

                              // Set payment and report fields from filing schedule
                              if (selectedPeriodData.payment !== undefined) {
                                setFieldValue(
                                  "payment",
                                  selectedPeriodData.payment,
                                );
                              }
                              if (selectedPeriodData.paymentType) {
                                setFieldValue(
                                  "paymentType",
                                  selectedPeriodData.paymentType,
                                );
                              }
                              if (selectedPeriodData.report !== undefined) {
                                setFieldValue(
                                  "report",
                                  selectedPeriodData.report,
                                );
                              }
                            } else {
                              // Clear due date if no period selected
                              setDueDateParts({ month: "", day: "", year: "" });
                              setFieldValue("dueDate", "");
                            }
                          }}
                          label="Period *"
                          IconComponent={ExpandMoreIcon}
                          disabled={!values.name}
                        >
                          <MenuItem value="">Select Period</MenuItem>
                          {availablePeriods && availablePeriods.length > 0 ? (
                            availablePeriods
                              .sort((a, b) => {
                                // Sort by due date chronologically (year first, then month)
                                const dateA = new Date(a.dueDate);
                                const dateB = new Date(b.dueDate);

                                // First compare by year
                                const yearA = dateA.getFullYear();
                                const yearB = dateB.getFullYear();
                                if (yearA !== yearB) {
                                  return yearA - yearB;
                                }

                                // Then compare by month within the same year
                                const monthA = dateA.getMonth();
                                const monthB = dateB.getMonth();
                                return monthA - monthB;
                              })
                              .map((periodData, index) => (
                                <MenuItem
                                  key={`${periodData.timePeriod}-${index}`}
                                  value={periodData.timePeriod}
                                >
                                  {periodData.timePeriod}
                                </MenuItem>
                              ))
                          ) : (
                            <MenuItem disabled>No periods available</MenuItem>
                          )}
                        </Select>
                        {errors.period && (
                          <FormHelperText sx={{ color: "red" }}>
                            {errors.period}
                          </FormHelperText>
                        )}
                      </FormControl>
                    ) : (
                      <FormControl fullWidth>
                        <FMTextField
                          name="period"
                          autoFocus
                          margin="dense"
                          disabled
                          label="Period"
                          placeholder="Period"
                          type="text"
                          fullWidth
                          variant="outlined"
                          sx={{ ...textFieldSx, width: "100%" }}
                        />
                      </FormControl>
                    )}
                  </FormControl>
                </Grid>

                {/* 6. Status - Dropdown with standard statuses */}
                {isStateReport && (
                  <Grid item xs={12} md={6}>
                    <FormControl sx={{ width: "100%" }}>
                      {isNewRecord ? (
                        <FormControl fullWidth>
                          <InputLabel>Status *</InputLabel>
                          <Select
                            name="status"
                            value={values.status || ""}
                            sx={selectFieldSx}
                            onChange={(e) => {
                              setFieldValue("status", e.target.value);
                            }}
                            label="Status *"
                            IconComponent={ExpandMoreIcon}
                          >
                            <MenuItem value="">Select Status</MenuItem>
                            <MenuItem value="Not Started">Not Started</MenuItem>
                            <MenuItem value="Waiting on Client">
                              Waiting on Client
                            </MenuItem>
                            <MenuItem value="Filed">Filed</MenuItem>
                            <MenuItem value="No Report Required">
                              No Report Required
                            </MenuItem>
                            {/* <MenuItem value="In Progress">In Progress</MenuItem>
                                                        <MenuItem value="Completed">Completed</MenuItem> */}
                          </Select>
                          {errors.status && (
                            <FormHelperText sx={{ color: "red" }}>
                              {errors.status}
                            </FormHelperText>
                          )}
                        </FormControl>
                      ) : (
                        <FormControl fullWidth>
                          <FMTextField
                            name="status"
                            autoFocus
                            margin="dense"
                            disabled
                            label="Status"
                            placeholder="Status"
                            type="text"
                            fullWidth
                            variant="outlined"
                            sx={{ ...textFieldSx, width: "100%" }}
                          />
                        </FormControl>
                      )}
                    </FormControl>
                  </Grid>
                )}

                {/* 7. Due Date - Date picker for past due reports */}
                {isStateReport && (
                  <Grid item xs={12} md={6}>
                    <FormControl sx={{ width: "100%" }}>
                      {isNewRecord ? (
                        <FormControl fullWidth>
                          <Grid container spacing={2}>
                            <Grid item xs={4}>
                              <TextField
                                label="Month"
                                value={dueDateParts.month}
                                disabled
                                fullWidth
                                variant="outlined"
                                placeholder="MM"
                                helperText="Locked"
                                sx={textFieldSx}
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                label="Day"
                                value={dueDateParts.day}
                                disabled
                                fullWidth
                                variant="outlined"
                                placeholder="DD"
                                helperText="Locked"
                                sx={textFieldSx}
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                label="Year *"
                                value={dueDateParts.year}
                                onChange={(e) => {
                                  const newYear = e.target.value;
                                  setDueDateParts((prev) => ({
                                    ...prev,
                                    year: newYear,
                                  }));

                                  // Update the combined due date for form validation
                                  if (
                                    dueDateParts.month &&
                                    dueDateParts.day &&
                                    newYear
                                  ) {
                                    const formattedDueDate = `${dueDateParts.month}/${dueDateParts.day}/${newYear}`;
                                    setFieldValue("dueDate", formattedDueDate);
                                  }
                                }}
                                fullWidth
                                variant="outlined"
                                placeholder="YYYY"
                                type="number"
                                sx={textFieldSx}
                                inputProps={{
                                  min: 2020,
                                  max: 2035,
                                }}
                                helperText={
                                  errors.dueDate ? (
                                    <span style={{ color: "red" }}>
                                      {errors.dueDate}
                                    </span>
                                  ) : (
                                    "Editable"
                                  )
                                }
                                disabled={
                                  !dueDateParts.month || !dueDateParts.day
                                }
                              />
                            </Grid>
                          </Grid>
                        </FormControl>
                      ) : (
                        <FormControl fullWidth>
                        <FMTextField
                          name="dueDate"
                          autoFocus
                          margin="dense"
                          disabled
                          label="Due Date"
                          placeholder="Due Date"
                          type="text"
                          fullWidth
                          variant="outlined"
                          sx={{ ...textFieldSx, width: "100%" }}
                        />
                        </FormControl>
                      )}
                    </FormControl>
                  </Grid>
                )}

                {isStateReport && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <FMTextField
                        name="notes"
                        autoFocus
                        margin="dense"
                        label="Notes To Client"
                        placeholder="Enter notes to client"
                        type="text"
                        fullWidth
                        variant="outlined"
                        sx={{ ...textFieldSx, width: "100%" }}
                      />
                    </FormControl>
                  </Grid>
                )}
                {isStateReport && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <FMTextField
                        name="taxAmount"
                        label="Tax Reported"
                        placeholder="Enter tax amount"
                        type="text"
                        fullWidth
                        variant="outlined"
                        sx={{ ...textFieldSx, width: "100%" }}
                        InputProps={{
                          inputComponent: NumericFormatCustom as any,
                        }}
                      />
                    </FormControl>
                  </Grid>
                )}
                {/* Address Field */}
                {isStateReport && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <FMTextField
                        name="address"
                        margin="dense"
                        label="Address"
                        placeholder="Enter address"
                        type="text"
                        fullWidth
                        variant="outlined"
                        sx={{ ...textFieldSx, width: "100%" }}
                      />
                    </FormControl>
                  </Grid>
                )}

                {/* Instructions Field */}
                {isStateReport && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <FMTextField
                        name="instructions"
                        margin="dense"
                        label="Instructions"
                        placeholder="Enter instructions"
                        type="text"
                        fullWidth
                        variant="outlined"
                        sx={{ ...textFieldSx, width: "100%" }}
                      />
                    </FormControl>
                  </Grid>
                )}

                {/* Additional Notes Field */}
                {isStateReport && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <FMTextField
                        name="additionalNotes"
                        margin="dense"
                        label="Additional Notes"
                        placeholder="Enter additional notes"
                        type="text"
                        fullWidth
                        variant="outlined"
                        sx={{ ...textFieldSx, width: "100%" }}
                      />
                    </FormControl>
                  </Grid>
                )}

                {isStateReport &&
                  completedStatuses.includes(values.status || "") && (
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            inputFormat="MM/DD/YYYY"
                            label="Date Completed *"
                            value={
                              values.dateFinalReportSentToClient
                                ? dayjs(values.dateFinalReportSentToClient)
                                : null
                            }
                            onChange={(newValue) => {
                              const formattedDate = newValue
                                ? newValue.format("MM/DD/YYYY")
                                : "";
                              setFieldValue(
                                "dateFinalReportSentToClient",
                                formattedDate,
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                fullWidth
                                size="small"
                                {...params}
                                sx={textFieldSx}
                                id="dateFinalReportSentToClient"
                                name="dateFinalReportSentToClient"
                                helperText={
                                  errors.dateFinalReportSentToClient ? (
                                    <span style={{ color: "red" }}>
                                      {errors.dateFinalReportSentToClient}
                                    </span>
                                  ) : (
                                    ""
                                  )
                                }
                              />
                            )}
                          />
                        </LocalizationProvider>
                      </FormControl>
                    </Grid>
                  )}

                {isStateReport && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Large Report</InputLabel>
                      <Select
                        name="largeReport"
                        size="small"
                        value={
                          values.largeReport
                            ? String(values.largeReport).toUpperCase()
                            : ""
                        }
                        sx={selectFieldSx}
                        onChange={(event) => {
                          setFieldValue("largeReport", event.target.value);
                          // Clear the largeReportFee when setting largeReport to FALSE
                          if (event.target.value === "FALSE") {
                            setFieldValue("largeReportFee", "");
                          }
                        }}
                        label="Large Report"
                        IconComponent={ExpandMoreIcon}
                        error={errors.largeReport ? true : false}
                      >
                        <MenuItem value="TRUE">Yes</MenuItem>
                        <MenuItem value="FALSE">No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {isStateReport &&
                  (values.largeReport === true ||
                    String(values.largeReport).toUpperCase() === "TRUE") && (
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth sx={{ '& .MuiFormControl-root': { marginTop: "0" } }}>
                        <TextField
                          name="largeReportFee"
                          autoFocus
                          margin="dense"
                          label="Large Report Fee *"
                          placeholder="Enter fee amount (e.g., 50.00)"
                          type="number"
                          fullWidth
                          variant="outlined"
                          value={values.largeReportFee || ""}
                          onChange={(e) =>
                            setFieldValue("largeReportFee", e.target.value)
                          }
                          sx={{ ...textFieldSx, width: "100%", '& .MuiInputBase-input': { padding: '3.5px 14px !important', textAlign: 'right' } }}
                          helperText={
                            errors.largeReportFee ? (
                              <span style={{ color: "red" }}>
                                {errors.largeReportFee}
                              </span>
                            ) : (
                              ""
                            )
                          }
                        />
                      </FormControl>
                    </Grid>
                  )}

                {!isStateReport && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <TextField
                        name="largeReportFee"
                        autoFocus
                        margin="dense"
                        label="Large Report Fee"
                        placeholder="Enter fee amount (e.g., 50.00)"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={values.largeReportFee || ""}
                        onChange={(e) =>
                          setFieldValue("largeReportFee", e.target.value)
                        }
                        sx={{ ...textFieldSx, width: "100%" }}
                        helperText={
                          errors.largeReportFee ? (
                            <span style={{ color: "red" }}>
                              {errors.largeReportFee}
                            </span>
                          ) : (
                            ""
                          )
                        }
                      />
                    </FormControl>
                  </Grid>
                )}
                    </Grid>
                  </Paper>
                </Grid>

                {/* Documents Table Section */}
                {documents &&
                  documents.length > 0 &&
                  (() => {
                    // Common styles
                    const headerCellStyle = {
                      fontWeight: "bold" as const,
                      backgroundColor: "#1976D2",
                      color: "#ffffff",
                      borderRight: "1px solid #1565c0",
                      borderTop: "none",
                      borderBottom: "2px solid #1565c0",
                      padding: "12px 24px",
                    };

                    const bodyCellStyle = {
                      borderBottom: "1px solid #e0e0e0",
                      borderRight: "1px solid #e0e0e0",
                      padding: "12px 24px",
                    };

                    const confirmDelete = async (
                      documentId: number | undefined,
                      index: number,
                    ) => {
                      const result = await Swal.fire({
                        title: "Delete Document?",
                        text: "Are you sure you want to delete this document? This action cannot be reverted.",
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonColor: "#d32f2f",
                        cancelButtonColor: "#757575",
                        confirmButtonText: "Yes, delete it!",
                        cancelButtonText: "Cancel",
                      });
                      if (result.isConfirmed) {
                        handleDeleteDocument(documentId, index);
                      }
                    };

                    const DeleteIcon = () => (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3 6H5M5 6H21M5 6V20C5 20.5304 5.21071 21.0391 5.58579 21.4142C5.96086 21.7893 6.46957 22 7 22H17C17.5304 22 18.0391 21.7893 18.4142 21.4142C18.7893 21.0391 19 20.5304 19 20V6H5ZM8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6"
                          stroke="#d32f2f"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    );

                    return (
                      <Grid item xs={12}>
                        <Box sx={{ margin: 2 }}>
                          <style>
                            {`
                                                        .attached-documents-table tr:first-child th:first-child {
                                                            border-top-left-radius: 0px !important;
                                                        }
                                                        .attached-documents-table tr:first-child th:last-child {
                                                            border-top-right-radius: 0px !important;
                                                        }
                                                        .attached-documents-table tr:last-child th:first-child {
                                                            border-bottom-left-radius: 0px !important;
                                                        }
                                                        .attached-documents-table tr:last-child th:last-child {
                                                            border-bottom-right-radius: 0px !important;
                                                        }
                                                    `}
                          </style>
                          <Typography variant="overline" sx={sectionTitleSx}>
                            Attached documents
                          </Typography>
                          <Paper
                            sx={{
                              width: "100%",
                              border: "1px solid #e0e0e0",
                              overflow: "hidden",
                              borderRadius: "4px",
                            }}
                          >
                            <TableContainer>
                              <Table className="attached-documents-table">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={headerCellStyle}>
                                      Document Name
                                    </TableCell>
                                    <TableCell sx={headerCellStyle}>
                                      Document Type
                                    </TableCell>
                                    <TableCell sx={headerCellStyle}>
                                      File Size (KB)
                                    </TableCell>
                                    <TableCell sx={headerCellStyle}>
                                      Actions
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {documents.map(
                                    (
                                      doc: StateReportDocument,
                                      index: number,
                                    ) => (
                                      <TableRow
                                        key={doc.stateReportDocumentId || index}
                                        sx={{
                                          backgroundColor:
                                            index % 2 === 0
                                              ? "#ffffff"
                                              : "#fafafa",
                                          "&:hover": {
                                            backgroundColor: "#f0f0f0",
                                          },
                                        }}
                                      >
                                        <TableCell sx={bodyCellStyle}>
                                          {doc.blobAddress ? (
                                            <Link
                                              href={doc.blobAddress}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              sx={{
                                                cursor: "pointer",
                                                textDecoration: "underline",
                                                color: "#1976d2",
                                                "&:hover": { color: "#1565c0" },
                                              }}
                                            >
                                              {doc.documentName || "-"}
                                            </Link>
                                          ) : (
                                            doc.documentName || "-"
                                          )}
                                        </TableCell>
                                        <TableCell sx={bodyCellStyle}>
                                          {doc.documentType || "-"}
                                        </TableCell>
                                        <TableCell sx={bodyCellStyle}>
                                          {doc.fileSizeKB !== undefined &&
                                          doc.fileSizeKB !== null
                                            ? `${doc.fileSizeKB} KB`
                                            : "-"}
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            ...bodyCellStyle,
                                            borderRight: "none",
                                            textAlign: "center",
                                          }}
                                        >
                                          <IconButton
                                            color="error"
                                            size="small"
                                            onClick={() =>
                                              confirmDelete(
                                                doc.stateReportDocumentId,
                                                index,
                                              )
                                            }
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                    ),
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Paper>
                        </Box>
                      </Grid>
                    );
                  })()}

                {/* Document Attachments Upload Section - Only show in Completed Queue (tab 2) */}
                {isStateReport && selectedTab === 2 && (
                  <Grid item xs={12}>
                    <Typography variant="overline" sx={sectionTitleSx}>
                      Document attachments
                    </Typography>
                    <Paper variant="outlined" sx={paperSectionSx}>
                      <FormHelperText
                        className="formtexthelper-heading"
                        sx={{ mb: 2, display: "block" }}
                      >
                        Please select a box to choose a file from your computer,
                        or drag and drop your file into the appropriate box
                        below:
                      </FormHelperText>

                      <Grid container spacing={2}>
                        {attachments.map((attachment, index) => (
                          <Grid item xs={12} sm={6} key={attachment.id}>
                            <Box>
                              <Box
                                sx={{
                                  marginBottom: "8px",
                                  fontWeight: "medium",
                                }}
                              >
                                {attachment.type}
                              </Box>
                              <FileDropzone
                                label={attachment.type}
                                onFileDrop={(file) =>
                                  handleFileDrop(index, file)
                                }
                                acceptedFile={attachment.file}
                                onViewFile={
                                  attachment.file ? handleViewFile : undefined
                                }
                              />
                              {attachment.file && (
                                <Box
                                  sx={{ marginTop: "8px", textAlign: "center" }}
                                >
                                  <Link
                                    component="button"
                                    variant="body2"
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      removeAttachment(index);
                                    }}
                                    sx={{ color: "#d32f2f", cursor: "pointer" }}
                                  >
                                    Remove
                                  </Link>
                                </Box>
                              )}
                            </Box>
                          </Grid>
                        ))}
                      </Grid>

                      {attachments.length < 12 && (
                        <Box sx={{ marginTop: "16px" }}>
                          <Link
                            component="button"
                            variant="body1"
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              addMoreAttachments();
                            }}
                            sx={{
                              color: "#1976D2",
                              cursor: "pointer",
                              textDecoration: "underline",
                              fontSize: "14px",
                              fontWeight: "medium",
                            }}
                          >
                            + Add More Supplemental Attachments
                          </Link>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions
              sx={{
                px: 3,
                py: 2,
                gap: 1.5,
                borderTop: 1,
                borderColor: "divider",
                backgroundColor: "grey.50",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="outlined"
                size="medium"
                onClick={handleClose}
                startIcon={<CloseOutlinedIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: "#e2e8f0",
                  color: "#475569",
                  "&:hover": {
                    borderColor: "#2099d2",
                    backgroundColor: "#2099d214",
                    color: "#2099d2",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="medium"
                type="submit"
                disabled={isSubmitting}
                startIcon={<SaveOutlinedIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  backgroundColor: "#2099d2",
                  "&:hover": { backgroundColor: "#176990" },
                  px: 2.5,
                }}
              >
                Submit
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>

      {/* Document Viewer Dialog */}
      <Dialog
        open={documentViewerOpen}
        onClose={handleCloseDocumentViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          style: {
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle className="card-title">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              sx={{
                flex: 1,
                fontWeight: "medium",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                paddingRight: "16px",
              }}
            >
              {documentViewerFileName || ""}
            </Box>
            <IconButton
              onClick={handleCloseDocumentViewer}
              sx={{
                color: "#666",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                  color: "#000",
                },
              }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ padding: 0, height: "80vh" }}>
          {documentViewerUrl && (
            <iframe
              src={documentViewerUrl}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
              }}
              title="Document Viewer"
            />
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};
